import { defineCollection } from "@rebasepro/cms-types";
import type { CollectionConfig } from "@rebasepro/types";
import playersCollection from "./players";
import { moment } from "../words";

const usersCollection = defineCollection({
    name: "Users",
    singularName: "User",
    slug: "users",
    auth: { enabled: true },
    table: "users",
    description: "Every account: Google, Apple, email, and the guests every phone and TV signs in as. Roles live here.",
    schema: "rebase",
    securityRules: [
        {
            operation: "select",
            roles: ["admin"]
        },
        {
            operations: ["insert", "update", "delete"],
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid",
            admin: {
                readOnly: true
            }
        },
        email: {
            name: "Email",
            type: "string",
            validation: {
                required: true,
                unique: true
            }
        },
        displayName: {
            name: "Name",
            type: "string",
            columnName: "display_name"
        },
        photoURL: {
            name: "Photo URL",
            type: "string",
            columnName: "photo_url",
            url: true,
            admin: { urlPreview: "image" }
        },
        roles: {
            name: "Roles",
            type: "array",
            columnType: "text[]",
            of: {
                name: "Role",
                type: "string",
                enum: {
                    admin: "Admin",
                    editor: "Editor",
                    viewer: "Viewer"
                }
            }
        },
        // set by Rebase auth: the guest every phone and TV signs in as (anon_…@anonymous.local)
        isAnonymous: {
            name: "Guest",
            type: "boolean",
            columnName: "is_anonymous",
            defaultValue: false,
            admin: {
                readOnly: true,
                hideFromCollection: true
            }
        },
        // what they did in SongBuzz: the player kept under "u:<this id>" (players.ts), rebuilt every five minutes
        player: {
            name: "Player",
            type: "relation",
            admin: { readOnly: true },
            relation: {
                kind: "via",
                cardinality: "one",
                target: (): CollectionConfig => playersCollection,
                relationName: "player",
                joinPath: [{ table: "songbuzz_players", on: { from: "id", to: "user_id" } }]
            }
        },
        passwordHash: {
            name: "Password Hash",
            type: "string",
            columnName: "password_hash",
            excludeFromApi: true,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerified: {
            name: "Email Verified",
            type: "boolean",
            columnName: "email_verified",
            defaultValue: false,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerificationToken: {
            name: "Email Verification Token",
            type: "string",
            columnName: "email_verification_token",
            excludeFromApi: true,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerificationSentAt: {
            name: "Email Verification Sent At",
            type: "date",
            columnName: "email_verification_sent_at",
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        metadata: {
            name: "Metadata",
            type: "map",
            keyValue: true,
            properties: {},
            defaultValue: {},
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        createdAt: {
            name: "Joined",
            type: "date",
            columnName: "created_at",
            autoValue: "on_create",
            admin: {
                readOnly: true
            }
        },
        updatedAt: {
            name: "Updated At",
            type: "date",
            columnName: "updated_at",
            autoValue: "on_update",
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        }
    },
    admin: {
        icon: "Users",
        group: "System",
        openEntityMode: "dialog",
        // a guest is every phone and TV that signed in without an account (anon_…@anonymous.local): say so
        display: {
            title: ({ entity }) => {
                const email = String(entity.values.email ?? "");
                return entity.values.displayName || (/@anonymous\.local$/i.test(email) ? "Guest" : email) || "No name";
            },
            // with columns the list shows no date of its own: when they joined goes in the row
            subtitle: ({ entity }) => {
                const email = String(entity.values.email ?? "");
                const joined = entity.values.createdAt && `joined ${moment(entity.values.createdAt)}`;
                if (/@anonymous\.local$/i.test(email)) return ["Signed in as a guest, no account", joined].filter(Boolean).join(" · ");
                return [entity.values.displayName ? email : "", joined].filter(Boolean).join(" · ") || undefined;
            },
            date: "createdAt",
        },
        disableDefaultActions: ["copy"],
        // whoever has a role first (no role is null, or an empty list), then the newest
        sort: [["roles", "desc", "last"], ["createdAt", "desc"]],
        // the guests are most of the rows: accounts only, until the filter is cleared
        defaultFilter: { isAnonymous: ["==", false] },
        filterPresets: [
            { label: "Accounts", filterValues: { isAnonymous: ["==", false] } },
            { label: "Guests", filterValues: { isAnonymous: ["==", true] } }
        ],
        // Joined is declared so the default sort doesn't add it as a column ahead of Player: it's the one dropped when
        // the row runs out of room, and the row text says it anyway
        listProperties: ["roles", "player", "createdAt"],
        propertiesOrder: [
            "id",
            "email",
            "displayName",
            "roles",
            "player",
            "createdAt"
        ]
    }
});

export default usersCollection;
